import { ChangeDetectorRef, Component, Input, Output, OnInit, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { take, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import _ from 'lodash';

import { AvailableProgramsViewModel } from '@Work/Shared/new-business-button/new-business-button.component';
import { PopupSettings } from '@progress/kendo-angular-popup';
import { ProducerSelectedEvent } from '@Shared/Components/sm-producer-search/sm-producer-search.component';
import { Guid } from '@Core/models/guid';
import { CAN_Province_Territories, US_States, LocationVM } from '@Core/enums/locations';
import { TenantPermissions } from '@Core/enums/codegen/tenant-permissions.enum';
import { Region } from '@Core/models/codegen/configuration.models';
import { UnityStepVM } from '@Shared/Components/unity-stepper/unity-stepper.component';
import { Program } from '@Core/models/codegen/area.models';
import { AuthService } from '@Services/auth.service';
import { FeatureFacetService } from '@Services/feature-facet.service';
import { PolicyServiceExt } from '@Services/policy.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AccountNumberDialogComponent } from '@Work/Shared/account-number-dialog/account-number-dialog.component';
import { AccountNumberFields, NumberingTaskType } from '@Core/models/account-number';
import { ACCOUNT_NUMBER_DIALOG_CANCEL } from '@Work/Shared/account-number-dialog/account-number-dialog.component';

@Component({
    standalone: false,
    selector: 'account-creation-modal',
    templateUrl: './account-creation-modal.component.html',
    styleUrls: ['./account-creation-modal.component.scss'],
})
export class AccountCreationModalComponent implements OnInit {
    @Input() programs: AvailableProgramsViewModel[] = [];
    @Input() errorMessage: string;
    @Input() regions: Region[];
    @Input() programRequired: boolean;

    @Output() onCreateAccount: EventEmitter<AccountCreationModalResult> = new EventEmitter();

    @ViewChild('nameInput') nameInput: any;
    @ViewChild('countryInput') countryInput: ElementRef;

    public TENANT_PERMISSIONS = TenantPermissions;
    public shellAccount: boolean;
    public program?: Program;

    // Fields on General Information (Step 1)
    public accountName: string;
    public accountCode: string;
    public dba: string;
    public fein: string;
    private _legalEntity: string;
    public get legalEntity(): string { return this._legalEntity; }
    public set legalEntity(value: string) {
        this._legalEntity = value;
        // Clear custom Other value when user selects anything other than 'Other'
        if (value !== 'Other') {
            this.otherLegalEntity = '';
        }
    }

    // When true, a child modal (account-number dialog) is open; used to make this modal inert
    public childModalOpen: boolean = false;
    public otherLegalEntity: string = '';
    public descriptionOfOperations: string;
    public producerName: string;

    // Fields on Contact Info (Step 2)
    public phone: string;
    public country: string;
    public address1: string;
    public address2: string;
    public address3: string;
    public city: string;
    public state: string;
    public zip: string;
    public county: string;
    public unincorporated: boolean;
    public taxLocation: string;

    public us_states: LocationVM[] = US_States.getStates();
    public can_provinces: LocationVM[] = CAN_Province_Territories.getProvincesAndTerritories();

    public legalEntities: string[] = [];
    public states$: Observable<string[]>;
    public cities$: Observable<string[]>;
    public zipCodes$: Observable<string[]>;
    public counties$: Observable<string[]>;

    public producerId: Guid;

    public popupSettings : PopupSettings;

    public accountCreationSteps: UnityStepVM[] = [
        { Name: "General Information"}, { Name: "Contact Information" }
    ];
    public accountCreationCurrentStep = 0;
    public isBureauEnabled: boolean = false;

    constructor(
        public authService: AuthService,
        public featureFacetService: FeatureFacetService,
        public policyService: PolicyServiceExt,
        public activeModal: NgbActiveModal,
        public cdRef: ChangeDetectorRef,
        private modalService: NgbModal
    ) { }

    ngOnInit(): void {
        this.shellAccount = false;

        if (this.regions?.length) {
            var defaultRegion = _.find(this.regions, r => r.IsProgramDefault);
            this.country = defaultRegion?.Country;
        }
        if (!this.country) {
            this.country = 'USA';
        }

        this.featureFacetService.getAllFacets().pipe(take(1)).subscribe(allFacets => {
            // Fetch legal entities here if Bureau Mode is enabled
            if (FeatureFacetService.checkIfFacetExists(TenantPermissions.Flag_Base_Work_Accounts_BureauMode, allFacets, 'work',
                this.authService.getAreaKey()))
            {
                this.isBureauEnabled = true;
                this.policyService.lookupLegalEntities().pipe(take(1)).subscribe(entities => {
                    this.legalEntities = entities;
                });

                this.updateGeneralCSZFields();
            }
        });
    }

    createAccount(startWorkflow: boolean) {
        if (this.disableCreateAccount())
            return;

        const emitResult = (selectedAccountCode?: string) => {
            const result: AccountCreationModalResult = {
                accountName: this.accountName,
                accountCode: selectedAccountCode ?? this.accountCode,
                producerId: this.producerId?.toString(),
                dba: this.dba,
                fein: this.fein,
                legalEntity: this.legalEntity,
                otherLegalEntity: this.legalEntity === 'Other' ? (this.otherLegalEntity?.trim() || '') : '',
                descriptionOfOperations: this.descriptionOfOperations,
                phone : this.phone,
                country: this.country,
                address1: this.address1,
                address2: this.address2,
                address3: this.address3,
                city: this.city,
                state: this.state,
                zip: this.zip,
                county: this.county,
                unincorporated: this.unincorporated,
                taxLocation: this.taxLocation,
                startWorkflow : startWorkflow,
                programId: this.program ? this.program.Id : null
            };
            this.onCreateAccount.emit(result);
        };

        if (this.isBureauEnabled) {

            // make this modal inert while the account-number dialog is open
            this.childModalOpen = true;

            const body: AccountNumberFields = {
                numberingTaskType: NumberingTaskType.Interactive1,
                requiredInput: { Name: this.accountName },
            };

            this.policyService.generateAccountNumber(body).pipe(
                take(1),
                catchError((err) => {
                    console.warn('Account number generation failed', err);
                    return of(null as any);
                }),
            ).subscribe((res: any) => {
                if (!res) {
                    return;
                }

                const editMask = res?.editMask ?? '';
                let validationMask: RegExp | null = null;
                try { validationMask = res?.validationMask ? new RegExp(res.validationMask) : null; } catch { validationMask = null; }

                try { (document.activeElement as HTMLElement)?.blur(); } catch { }

                const modalRef = this.modalService.open(AccountNumberDialogComponent, { centered: true });
                const instance = modalRef.componentInstance as AccountNumberDialogComponent;
                instance.defaultNumber = res?.id ?? '';
                instance.helpText = res?.helpText ?? '';
                instance.editMask = editMask;
                instance.validationMask = validationMask ?? /.+/;

                modalRef.result.then((selected: string) => {

                // If the dialog returned the cancel sentinel, just return to the account creation modal
                if (selected === ACCOUNT_NUMBER_DIALOG_CANCEL) {
                    // do nothing - user cancelled the account-number confirmation
                    this.childModalOpen = false;
                    return;
                }

                if (selected) {
                    this.accountCode = selected;
                    emitResult(this.accountCode);
                } else {
                    return;
                }
            }).catch(() => {
                    this.childModalOpen = false;
                    return;
                });
            });
        } else {
            emitResult();
        }
    }

    public setProducer(event: ProducerSelectedEvent): void {
        this.producerId = event.Id;
        this.producerName = `${event.Name} (${event.Code})`;
    }

    public disableNext() {
        return !this.accountName || (this.programRequired && !this.program);
    }

    public disableCreateAccount() {
        if (this.childModalOpen)
            return true;

        var actualFein = this.fein?.replace('-', '')?.trim();
        return !this.address1 || !this.phone || !this.city  || (!this.state && this.country != 'GBR')
            || !this.zip || (this.country == 'USA' && actualFein?.length && actualFein.length != 9);
    }

    public next(e?: any) {
        if (this.disableNext())
            return;

        this.accountCreationCurrentStep = 1;
        this.cdRef.detectChanges();
        this.countryInput.nativeElement.focus();

        e?.stopPropagation();
    }

    public changeStep(step: number) {
        // We should only be changing from 1 => 0
        if (step == 0)
            this.back();
    }

    public back() {
        this.accountCreationCurrentStep = 0;
        this.cdRef.detectChanges();
        this.nameInput.nativeElement.focus();
    }

    updateGeneralCSZFields() {
        const city = this.city ?? '';
        const state = this.state ?? '';
        const zip = this.zip ?? '';
        const county = this.county ?? '';

        this.cities$ = this.policyService.lookupCities(state, zip, county);
        this.states$ = this.policyService.lookupStates(city, zip, county);
        this.zipCodes$ = this.policyService.lookupZipCodes(city, state, county);
        this.counties$ = this.policyService.lookupCounties(city, state, zip);
    }

    updateOneAndOnlyOneGeneralStatesField() {
        this.states$.pipe(take(1)).subscribe(states => {
            if (states.length === 1 && states[0] !== '') {
                this.state = states[0];
            } else if (states.length === 0 || !states.includes(this.state)) {
                this.state = '';
            }
        });
    }

    updateOneAndOnlyOneGeneralCitiesField() {
        this.cities$.pipe(take(1)).subscribe(cities => {
            if (cities.length === 1 && cities[0] !== '') {
                this.city = cities[0];
            } else if (cities.length === 0 || !cities.includes(this.city)) {
                this.city = '';
            }
        });
    }

    updateOneAndOnlyOneGeneralZipCodesField() {
        this.zipCodes$.pipe(take(1)).subscribe(zipCodes => {
            if (zipCodes.length === 1 && zipCodes[0] !== '') {
                this.zip = zipCodes[0];
            } else if (zipCodes.length === 0 || !zipCodes.includes(this.zip)) {
                this.zip = '';
            }
        });
    }

    updateOneAndOnlyOneGeneralCountiesField() {
        this.counties$.pipe(take(1)).subscribe(counties => {
            if (counties.length === 1 && counties[0] !== '') {
                this.county = counties[0];
            } else if (counties.length === 0 || !counties.includes(this.county)) {
                this.county = '';
            }
        });
    }


    updateOneAndOnlyOneGeneralCSZFields(attributeName: string) {
        this.updateGeneralCSZFields();

        if (attributeName === 'zipCode') {
            this.updateOneAndOnlyOneGeneralStatesField();
            this.updateOneAndOnlyOneGeneralCountiesField();
            this.updateOneAndOnlyOneGeneralCitiesField();
        } else if (attributeName === 'state') {
            this.updateOneAndOnlyOneGeneralZipCodesField();
            this.updateOneAndOnlyOneGeneralCountiesField();
            this.updateOneAndOnlyOneGeneralCitiesField();
        } else if (attributeName === 'city') {
            this.updateOneAndOnlyOneGeneralZipCodesField();
            this.updateOneAndOnlyOneGeneralCountiesField();
            this.updateOneAndOnlyOneGeneralStatesField();
        } else if (attributeName === 'county') {
            this.updateOneAndOnlyOneGeneralZipCodesField();
            this.updateOneAndOnlyOneGeneralStatesField();
            this.updateOneAndOnlyOneGeneralCitiesField();
        }
    }
}


export interface AccountCreationModalResult {
    accountName: string,
    accountCode: string,
    producerId: string,
    dba: string;
    fein: string;
    legalEntity: string;
    otherLegalEntity: string;
    descriptionOfOperations: string;
    phone: string;
    country: string;
    address1: string;
    address2: string;
    address3: string;
    city: string;
    state: string;
    zip: string;
    county: string;
    unincorporated: boolean;
    taxLocation: string;
    startWorkflow: boolean;
    programId: string;
}
